import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Globe, 
  Plane, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Package,
  ShieldCheck,
  FileText,
  Truck,
  Building
} from "lucide-react";

const InternationalerVersandPage = () => {
  const services = [
    {
      icon: Plane,
      title: "Luftfracht weltweit",
      description: "Express-Luftfracht in 180 Länder. Eilige Sendungen erreichen ihr Ziel in kürzester Zeit."
    },
    {
      icon: Globe,
      title: "180 Länder weltweit",
      description: "Von Europa bis Asien, von Amerika bis Australien – wir versenden in fast jedes Land der Welt."
    },
    {
      icon: Clock,
      title: "Same-Day bis 12 Uhr",
      description: "Bestellungen bis 12 Uhr werden noch am selben Tag auf den Weg gebracht."
    },
    {
      icon: FileText,
      title: "Zollabwicklung inklusive",
      description: "Wir übernehmen die komplette Zolldokumentation und -abwicklung für Sie."
    },
    {
      icon: ShieldCheck,
      title: "Versicherungsschutz",
      description: "Umfassender Versicherungsschutz für alle internationalen Sendungen."
    },
    {
      icon: Package,
      title: "Ex Works Service",
      description: "Für internationale Kunden: Wir organisieren Abholung und Versand aus Deutschland."
    }
  ];

  const partners = [
    "UPS", "DHL Express", "FedEx", "DPD", "GLS", "TNT"
  ];

  const regions = [
    { name: "Europa", countries: "40+ Länder", time: "1-3 Werktage" },
    { name: "Nordamerika", countries: "USA, Kanada, Mexiko", time: "2-5 Werktage" },
    { name: "Asien", countries: "China, Japan, Südkorea, etc.", time: "3-7 Werktage" },
    { name: "Rest der Welt", countries: "180 Länder gesamt", time: "3-10 Werktage" }
  ];

  return (
    <div data-testid="internationaler-versand-page">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <span className="inline-block bg-orange-600 text-white text-sm uppercase tracking-widest font-bold px-4 py-2 mb-6">
              Internationaler B2B Versand
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Versand in <span className="text-orange-500">180 Länder</span> weltweit
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl">
              Internationaler Versand für B2B-Unternehmen. Express-Luftfracht, Zollabwicklung 
              und Sendungsverfolgung – alles aus einer Hand.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/angebot">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-wider font-bold px-10 py-6 text-lg">
                  Versandangebot anfordern
                </Button>
              </Link>
              <Link to="/kontakt">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 uppercase tracking-wider font-bold px-10 py-6 text-lg bg-transparent">
                  Beratung anfragen
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Bar */}
      <section className="py-8 bg-slate-100 border-b border-slate-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <span className="text-slate-500 font-medium">Unsere Partner:</span>
            {partners.map((partner) => (
              <span key={partner} className="text-slate-700 font-bold text-lg">
                {partner}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-6 bg-orange-600 text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-center">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} />
              <span className="font-bold">180 Länder weltweit</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} />
              <span className="font-bold">Express-Luftfracht</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} />
              <span className="font-bold">Zollabwicklung inklusive</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} />
              <span className="font-bold">24/7 Sendungsverfolgung</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
              Unsere Leistungen
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 text-slate-900">
              Internationaler Versand – rundum sorglos
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div 
                  key={service.title}
                  className="p-8 border border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all"
                  data-testid={`service-${index}`}
                >
                  <div className="w-14 h-14 bg-orange-100 flex items-center justify-center mb-6">
                    <Icon className="text-orange-600 w-7 h-7" />
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-slate-900">{service.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Regions Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
              Lieferzeiten
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 text-slate-900">
              Schnelle Lieferung weltweit
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {regions.map((region, index) => (
              <div key={region.name} className="bg-white p-6 border border-slate-200 text-center">
                <Globe className="w-10 h-10 text-orange-600 mx-auto mb-4" />
                <h3 className="font-bold text-xl mb-2 text-slate-900">{region.name}</h3>
                <p className="text-slate-600 text-sm mb-3">{region.countries}</p>
                <div className="bg-orange-100 text-orange-700 font-bold py-2 px-4 rounded">
                  {region.time}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ex Works Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
                Für internationale Kunden
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-6 text-slate-900">
                Ex Works Service aus Deutschland
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Sie bestellen Waren in Deutschland und benötigen einen zuverlässigen Partner 
                für Abholung und Versand? Wir organisieren alles für Sie:
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-orange-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-700">Abholung bei Ihrem Lieferanten in Deutschland</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-orange-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-700">Konsolidierung mehrerer Sendungen</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-orange-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-700">Komplette Zollabwicklung und Export-Dokumentation</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-orange-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-700">Versand per Luftfracht oder Seefracht weltweit</span>
                </div>
              </div>

              <Link to="/angebot">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-wider font-bold px-8">
                  Ex Works Angebot anfordern
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </div>
            
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/ed08gqs9_Luftfracht_2.jpg"
                alt="Luftfracht international"
                className="w-full shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Internationaler Versand leicht gemacht
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Fordern Sie jetzt Ihr individuelles Versandangebot an. 
            Wir beraten Sie gerne zu den besten Optionen für Ihre Sendungen.
          </p>
          <Link to="/angebot">
            <Button className="bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-wider font-bold px-12 py-6 text-lg">
              Kostenloses Angebot anfordern
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default InternationalerVersandPage;
